import { email as supportEmail, website } from '@getpopsure/private-constants';
import { Button, InformationBox, Input } from '@popsure/dirty-swan';
import { setAccountDataInQuestionnaire } from 'actions/account';
import classNames from 'classnames';
import { GoogleLoginButton } from 'features/GoogleOAuth';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from 'reducers';
import { getRequestError, getRequestInProcess } from 'selectors/requests';
import { getEmail } from 'selectors/user';
import { useSafeTranslation } from 'shared/i18n';
import { emailAddressValidator } from 'shared/util/emailValidator';

import styles from './EmailForm.module.scss';

interface Props {
  email: string;
  loading?: boolean;
  error?: unknown;
  onInputChange(email: string): void;
  onSubmit(): void;
}

const Email = ({ email, loading, error, onInputChange, onSubmit }: Props) => {
  const { t } = useSafeTranslation();
  const dispatch = useDispatch();
  const accountEmail = useSelector(getEmail);
  const loadingGoogle = useSelector((state: AppState) =>
    getRequestInProcess(state, 'SET_ACCOUNT_DATA_IN_QUESTIONNAIRE')
  );
  const errorGoogle = useSelector((state: AppState) =>
    getRequestError(state, 'SET_ACCOUNT_DATA_IN_QUESTIONNAIRE')
  );
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isLoginWithGoogle, setIsLoginWithGoogle] = useState(false);
  const [continueWithEmail, setContinueWithEmail] = useState(false);
  const [googleLoginError, setGoogleLoginError] = useState(false);

  const valid = emailAddressValidator(email) && termsAccepted;

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    onInputChange(e.target.value);
  };

  const handleSetTermsAccepted = () => {
    setTermsAccepted((value) => !value);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (valid) {
      onSubmit();
    }
  };

  const handleOnGoogleLogin = () => {
    dispatch(
      setAccountDataInQuestionnaire(
        '/recommendation-tool/quickRecommendation/questionnaire/personal-info/quick-financial-responsibilities',
        'userCreation'
      )
    );
    setIsLoginWithGoogle(true);
  };

  useEffect(() => {
    if (!errorGoogle && !loadingGoogle && accountEmail && isLoginWithGoogle) {
      setIsLoginWithGoogle(false);
      onSubmit();
    }
  }, [errorGoogle, loadingGoogle, accountEmail, onSubmit, isLoginWithGoogle]);

  return (
    <form className={styles.container} onSubmit={handleSubmit}>
      <div className="p-body">
        <h1 className="p-h1 mb8">
          {t(
            'components.email.titleContinueToFeatherAccount',
            'Let’s continue with a Feather account'
          )}
        </h1>
        <div className="p-p mb24">
          {t(
            'components.email.initial',
            'We’ll create one if you’re new to Feather.'
          )}
        </div>

        <AnimateHeight duration={300} height={continueWithEmail ? 'auto' : 0}>
          <Input
            className="wmx6"
            type="email"
            autoComplete="email"
            value={email}
            placeholder="Email address"
            onChange={handleInputChange}
            required
            data-cy="input-email"
          />
          <div className="mt16 mb24 p-label-container">
            <input
              id="terms"
              className="p-checkbox"
              type="checkbox"
              onChange={handleSetTermsAccepted}
            />
            <label
              htmlFor="terms"
              className={`p-label ${styles.label}`}
              data-cy="email-tnc"
            >
              <div className="p-p">
                I have read and understood the{' '}
                <a
                  className="p-a"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={website.privacyPolicy}
                >
                  privacy policy
                </a>{' '}
                and{' '}
                <a
                  className="p-a"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={website.termsAndConditions}
                >
                  T&Cs
                </a>
                .
              </div>
            </label>
          </div>
        </AnimateHeight>

        <AnimateHeight duration={300} height={error ? 'auto' : 0}>
          <div className="p-p p-notice--warning mt16">
            {`An error occurred while submitting your email address. Please try
            again or contact us at ${supportEmail.help}`}
          </div>
        </AnimateHeight>

        <div className={classNames(styles.buttonContainer, 'd-flex gap8')}>
          {continueWithEmail ? (
            <Button
              data-cy="email-form-submit"
              className={classNames(`wmn3 ${styles.button}`)}
              disabled={!valid}
              type="submit"
              loading={loading}
            >
              {t('components.email.button', 'Continue')}
            </Button>
          ) : (
            <Button
              className="wmn3"
              data-cy="button-continue-with-email"
              onClick={() => setContinueWithEmail(true)}
            >
              {t('components.email.buttonWithEmail', 'Continue with email')}
            </Button>
          )}

          <GoogleLoginButton
            className="wmn3"
            onSuccess={() => handleOnGoogleLogin()}
            onLoginError={() => {
              setIsLoginWithGoogle(false);
              setGoogleLoginError(true);
            }}
          />
        </div>

        {googleLoginError && (
          <div>
            <InformationBox className="mt16 wmx6" variant="error">
              {t(
                'components.email.googleLoginError',
                "Your account isn't linked to Google yet - please log in with your email and sign-in code first, then link your Google account in Account settings."
              )}
            </InformationBox>
          </div>
        )}
      </div>
    </form>
  );
};

export default Email;
